import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { eps } from './url.service'
import { Observable } from 'rxjs'
import {
  RunningPayrollBatch,
  IncomingPayrollBatch,
  IncomingRunningPayrollBatch,
  IncomingPayroll,
  TdsCalculationBatchInput,
  TdsCalculationResult,
  TdsCalculationInput
} from '../store/payroll/payroll.state'
import { EmployeeSalary } from '../store/employee-salary/employee-salary.state'

@Injectable({
  providedIn: 'root'
})
export class PayrollService {
  private http = inject(HttpClient)
  private payrollApi = eps.hrm.runPayrollApi
  private managePayrollApi = eps.hrm.managePayrollApi
  private salaryApi = eps.hrm.createSalaryApi

  // Load all payroll batches
  loadPayrollBatches(financialYear: string, belongsTo: string): Observable<IncomingPayrollBatch[]> {
    return this.http.get<IncomingPayrollBatch[]>(this.managePayrollApi, {
      params: { financialYear, belongsTo }
    })
  }

  // Load running payroll batch
  loadRunningPayrollBatch(
    payPeriodStart: string,
    payPeriodEnd: string,
    belongsTo: string
  ): Observable<IncomingRunningPayrollBatch> {
    return this.http.get<IncomingRunningPayrollBatch>(this.payrollApi, {
      params: { payPeriodStart, payPeriodEnd, businessId: belongsTo }
    })
  }

  loadPreviousSalaries(
    requests: { employeeId: string; currentSalaryEffectiveFrom: string }[]
  ): Observable<Record<string, EmployeeSalary | null>> {
    return this.http.post<Record<string, EmployeeSalary | null>>(
      `${this.salaryApi}/previous-salaries`,
      requests
    )
  }

  loadArrearLopDays(
    requests: { employeeId: string; businessId: string; periodStart: string; periodEnd: string }[]
  ): Observable<Record<string, number>> {
    return this.http.post<Record<string, number>>(`${this.payrollApi}/arrear-lop-days`, requests)
  }

  loadBatchTdsPreview(
    payload: TdsCalculationBatchInput
  ): Observable<Record<string, TdsCalculationResult>> {
    return this.http.post<Record<string, TdsCalculationResult>>(
      `${this.payrollApi}/batch-tds-preview`,
      payload
    )
  }

  loadTdsPreview(
    payload: TdsCalculationInput
  ): Observable<{ result: TdsCalculationResult; employeeId: string }> {
    return this.http.post<{ result: TdsCalculationResult; employeeId: string }>(
      `${this.payrollApi}/tds-preview`,
      payload
    )
  }

  upsertRunningPayrollBatch(
    batchData: RunningPayrollBatch
  ): Observable<IncomingRunningPayrollBatch> {
    return this.http.put<IncomingRunningPayrollBatch>(this.payrollApi, batchData)
  }

  updatePayrollStatus(data: {
    id: string
    status: string
    payrollIds: string[]
  }): Observable<IncomingRunningPayrollBatch> {
    return this.http.put<IncomingRunningPayrollBatch>(`${this.payrollApi}/status`, data)
  }

  deletePayrollBatch(id: string): Observable<{ id: string }> {
    return this.http.delete<{ id: string }>(`${this.payrollApi}/${id}`)
  }

  getUserPayrollData(
    payPeriodStart: string,
    payPeriodEnd: string,
    employeeId: string
  ): Observable<IncomingPayroll> {
    return this.http.get<IncomingPayroll>(`${this.payrollApi}/employee`, {
      params: { payPeriodStart, payPeriodEnd, employeeId }
    })
  }

  getLatestUserPayrollDate(employeeId: string): Observable<{ latestPayrollDate: string }> {
    return this.http.get<{ latestPayrollDate: string }>(`${this.payrollApi}/latest-payroll-date`, {
      params: { employeeId }
    })
  }

  finalizePayrollBatch(
    batchId: string,
    dateOfPayment: string,
    modeOfPayment: string,
    paidBy: string
  ): Observable<IncomingPayrollBatch> {
    return this.http.put<IncomingPayrollBatch>(`${this.payrollApi}/finalize`, {
      batchId,
      dateOfPayment,
      modeOfPayment,
      paidBy
    })
  }
}
